import { Injectable, OnDestroy, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ComponentStore } from '@ngrx/component-store';
import {
  ChartFacade,
  ChartId,
  PerioChartFacade,
} from '@principle-theorem/ng-clinical-charting/store';
import {
  GlobalStoreService,
  PerioSettingsService,
} from '@principle-theorem/ng-principle-shared';
import {
  IChartedCondition,
  IChartedConditionWithResolvedConfig,
  IPerioRecord,
  IPerioTable,
  PerioTableSide,
  SwitchDirection,
  SwitchPattern,
  type ITooth,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  filterUndefined,
  snapshot,
} from '@principle-theorem/shared';
import { isEqual, last } from 'lodash';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  scan,
  switchMap,
  tap,
} from 'rxjs/operators';
import { PerioTableNavigation } from '../perio/perio-navigation';
import { PerioTable } from '../perio/perio-table';

interface INextCell {
  cellIndex?: number;
  key?: KeyboardEvent;
}

export interface INavigationMatrix {
  navigationGrid: (number | undefined)[][];
  rowBoundaries: {
    start: number;
    end: number;
  }[];
}

interface IModifiedPerioTables {
  perioTables: IPerioTable[];
  switchPattern: SwitchPattern;
  navigationKey: string;
}

interface INavigationRebuildState {
  current: IModifiedPerioTables | undefined;
  navigationChanged: boolean;
}

export interface IPerioChartState {
  perioTables: IPerioTable[];
  defaultPerioTables: IPerioTable[] | undefined;
  compareWithTables: IPerioTable[];
  navByTable: INavigationMatrix;
  navBySwitchPattern: (number | undefined)[][];
  nextCell: INextCell;
  direction: SwitchDirection;
  navHistory: number[];
  canEdit: boolean;
  teeth: ITooth[];
  perioRecords: IPerioRecord[];
  conditions: IChartedConditionWithResolvedConfig[];
}

const initialState: IPerioChartState = {
  perioTables: [],
  defaultPerioTables: undefined,
  compareWithTables: [],
  navByTable: {
    navigationGrid: [],
    rowBoundaries: [],
  },
  navBySwitchPattern: [],
  nextCell: { cellIndex: undefined, key: undefined },
  direction: SwitchDirection.Default,
  navHistory: [],
  canEdit: false,
  teeth: [],
  perioRecords: [],
  conditions: [],
};

@Injectable()
export class PerioChartStore
  extends ComponentStore<IPerioChartState>
  implements OnDestroy
{
  private _settings = inject(PerioSettingsService);
  private _chart = inject(ChartFacade);
  private _perioFacade = inject(PerioChartFacade);
  private _globalStore = inject(GlobalStoreService);
  private readonly _defaultPerioTables$ = this.select(
    (state) => state.defaultPerioTables
  ).pipe(filterUndefined(), distinctUntilChanged<IPerioTable[]>(isEqual));

  readonly perioTables$ = this.select((state) => state.perioTables);
  readonly navByTable$ = this.select((state) => state.navByTable);
  readonly navBySwitchPattern$ = this.select(
    (state) => state.navBySwitchPattern
  );
  readonly navHistory$ = this.select((state) => state.navHistory);
  readonly canEdit$ = this.select((state) => state.canEdit);
  readonly nextCell$ = this.select((state) => state.nextCell);
  readonly direction$ = this.select((state) => state.direction);
  readonly teeth$ = this.select((state) => state.teeth);
  readonly perioRecords$ = this.select((state) => state.perioRecords);
  readonly conditions$ = this.select((state) => state.conditions);
  readonly compareWithTables$ = this.select((state) => state.compareWithTables);

  readonly setPerioTables = this.updater(
    (state, perioTables: IPerioTable[]) => ({
      ...state,
      perioTables,
    })
  );

  readonly setDefaultPerioTables = this.updater(
    (state, defaultPerioTables: IPerioTable[]) => ({
      ...state,
      defaultPerioTables,
    })
  );

  readonly setNavBySwitchPattern = this.updater(
    (state, navBySwitchPattern: (number | undefined)[][]) => ({
      ...state,
      navBySwitchPattern,
    })
  );

  readonly setNextCell = this.updater((state, nextCell: INextCell) => ({
    ...state,
    nextCell,
  }));

  readonly setDirection = this.updater((state, direction: SwitchDirection) => ({
    ...state,
    direction,
  }));

  readonly clearNextCell = this.updater((state) => ({
    ...state,
    nextCell: { cellIndex: undefined, key: undefined },
  }));

  readonly addToNavHistory = this.updater((state, index: number) => {
    return {
      ...state,
      navHistory: [...state.navHistory, index],
    };
  });

  readonly removeFromNavHistory = this.updater((state) => ({
    ...state,
    navHistory: state.navHistory.slice(0, -1),
  }));

  constructor() {
    super(initialState);
    this._setTeeth$();
    this._setPerioRecords$();
    this._setConditions$();
    this._setCanEdit$();
    this._initialiseDefaultPerioTables$();
    this._buildModifiedPerioTables$();
    this._buildComparePerioTables$();
  }

  previousIndex(): number | undefined {
    const lastIndex = last(this.get().navHistory);
    this.removeFromNavHistory();
    return lastIndex;
  }

  private _setTeeth$(): void {
    this.effect(() =>
      this._chart.clinicalChartState$(ChartId.InAppointment).pipe(
        filterUndefined(),
        map((chart) => chart.teeth),
        distinctUntilChanged<ITooth[]>(isEqual),
        tap((teeth) => this.patchState({ teeth }))
      )
    );
  }

  private _setConditions$(): void {
    this.effect(() =>
      this._chart.clinicalChartState$(ChartId.InAppointment).pipe(
        filterUndefined(),
        switchMap((chart) => this._resolveConditions(chart.conditions)),
        distinctUntilChanged<IChartedConditionWithResolvedConfig[]>(isEqual),
        tap((conditions) => this.patchState({ conditions }))
      )
    );
  }

  private _setPerioRecords$(): void {
    this.effect(() =>
      this._chart.clinicalChartState$(ChartId.InAppointment).pipe(
        filterUndefined(),
        map((chart) => chart.perioRecords),
        map((records) =>
          PerioTable.addClinicalAttachmentLossMeasurements(records)
        ),
        filterUndefined(),
        distinctUntilChanged<IPerioRecord[]>(isEqual),
        tap((perioRecords) => this.patchState({ perioRecords }))
      )
    );
  }

  private _setCanEdit$(): void {
    this.effect(() =>
      this._chart.clinicalChartState$(ChartId.InAppointment).pipe(
        filterUndefined(),
        map((chart) => !chart.immutable),
        distinctUntilChanged<boolean>(isEqual),
        tap((canEdit) => this.patchState({ canEdit }))
      )
    );
  }

  private _initialiseDefaultPerioTables$(): void {
    this.effect(() =>
      combineLatest([
        this.teeth$,
        this.conditions$,
        this.canEdit$.pipe(map((canEdit) => !canEdit)),
        this._chart.chartView$(ChartId.InAppointment),
        this._chart.chartSection$(ChartId.InAppointment),
        this.perioRecords$,
      ]).pipe(
        filter((params) => !params.some((param) => param === undefined)),
        map((params) => PerioTable.toPerioTables(...params)),
        map(this._splitTablesBySide),
        tap((perioTables) => this.setDefaultPerioTables(perioTables)),
        catchError((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to load perio tables:', error);
          return of([]);
        })
      )
    );
  }

  private _buildComparePerioTables$(): void {
    this.effect(() =>
      toObservable(this._perioFacade.compareChart).pipe(
        switchMap((chart) => {
          if (!chart) {
            return of(undefined);
          }
          return combineLatest([
            of(chart.teeth),
            of(chart.conditions).pipe(
              switchMap((conditions) => this._resolveConditions(conditions))
            ),
            of(chart.immutable),
            this._chart.chartView$(ChartId.InAppointment),
            this._chart.chartSection$(ChartId.InAppointment),
            of(chart.perioRecords).pipe(
              map(PerioTable.addClinicalAttachmentLossMeasurements)
            ),
          ]).pipe(
            filter((params) => !params.some((param) => param === undefined)),
            map((params) => PerioTable.toPerioTables(...params)),
            map((tables) => this._splitTablesBySide(tables))
          );
        }),
        tap((compareWithTables) => this.patchState({ compareWithTables })),
        catchError((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to load perio tables:', error);
          return of([]);
        })
      )
    );
  }

  private _buildModifiedPerioTables$(): void {
    this.effect(() =>
      combineLatest([
        this._defaultPerioTables$,
        this.compareWithTables$.pipe(
          distinctUntilChanged<IPerioTable[]>(isEqual)
        ),
        this.teeth$.pipe(distinctUntilChanged<ITooth[]>(isEqual)),
        toObservable(this._settings.switchPattern).pipe(
          distinctUntilChanged<SwitchPattern>(isEqual)
        ),
      ]).pipe(
        map(
          ([
            tables,
            compareWithTables,
            teeth,
            switchPattern,
          ]): IModifiedPerioTables => {
            const mergedTables = PerioTable.buildAndMergePerioTables(
              tables,
              teeth,
              compareWithTables
            );
            const perioTables =
              PerioTableNavigation.assignCellProperties(mergedTables);
            return {
              perioTables,
              switchPattern,
              navigationKey: `${switchPattern}|${PerioTableNavigation.getNavigationStructureKey(
                perioTables
              )}`,
            };
          }
        ),
        scan<IModifiedPerioTables, INavigationRebuildState>(
          (previous, current) => ({
            current,
            navigationChanged:
              previous.current?.navigationKey !== current.navigationKey,
          }),
          { current: undefined, navigationChanged: true }
        ),
        tap(({ current, navigationChanged }) => {
          if (!current) {
            return;
          }
          this.patchState({
            perioTables: PerioTableNavigation.removeEmptyCells(
              current.perioTables
            ),
            ...(navigationChanged && {
              navByTable: PerioTableNavigation.buildNavByTable(
                current.perioTables
              ),
              navBySwitchPattern:
                current.switchPattern === SwitchPattern.Quadrant
                  ? PerioTableNavigation.buildNavByRow(current.perioTables)
                  : PerioTableNavigation.buildNavByMeasurement(
                      current.perioTables
                    ),
            }),
          });
        })
      )
    );
  }

  private _splitTablesBySide(tables: IPerioTable[]): IPerioTable[] {
    return tables.flatMap((table) => [
      PerioTable.filterTableBySide(table, PerioTableSide.Facial),
      PerioTable.filterTableBySide(table, PerioTableSide.Palatal),
    ]);
  }

  private async _resolveConditions(
    conditions: IChartedCondition[]
  ): Promise<IChartedConditionWithResolvedConfig[]> {
    if (!conditions) {
      return [];
    }
    try {
      const resolvedConditions = await asyncForEach(
        conditions,
        async (condition) => ({
          ...condition,
          config: await snapshot(
            this._globalStore.getConditionConfiguration$(condition.config.ref)
          ),
        })
      );
      return resolvedConditions.filter(
        (condition) => condition.config !== undefined
      ) as IChartedConditionWithResolvedConfig[];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error resolving conditions:', error);
      return [];
    }
  }
}
